/// <amd-module name="Wit/BoardsHub/KanbanBoard/Utils/TestAnnotationUtils" />

import { Outcome, TestPointUpdateParams } from "TestPlanCommon/Contracts/TestPlan";
import { getTFSProject } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { issueRequest } from "VSS/Platform/Fetch";
import { IVssPageService } from "VSS/Platform/Page";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { ITestAnnotationInfo, ITestItem, TestOutcome } from "Wit/BoardsHub/Common/Interfaces";
import * as Resources from "Wit/BoardsHub/KanbanBoard/Resources";

export function compareTestItems(a: ITestItem, b: ITestItem) {
    return (a.sequenceNumber ?? Number.MAX_SAFE_INTEGER) - (b.sequenceNumber ?? Number.MAX_SAFE_INTEGER);
}

export function getTestPointUpdate(testItem: ITestItem, outcome: TestOutcome): TestPointUpdateParams {
    // update API throws if you pass false here -_-
    const isActive = outcome === TestOutcome.Active ? true : undefined;
    return {
        id: testItem.testPointId,
        isActive,
        results: isActive ? undefined : { outcome: webApiOutcomeFromTestOutcome(outcome) }
    } as TestPointUpdateParams;
}

export function webApiOutcomeFromTestOutcome(outcome: TestOutcome): Outcome {
    switch (outcome) {
        case TestOutcome.Passed:
            return Outcome.Passed;
        case TestOutcome.Failed:
            return Outcome.Failed;
        case TestOutcome.Blocked:
            return Outcome.Blocked;
        case TestOutcome.NotApplicable:
            return Outcome.NotApplicable;
        case TestOutcome.Paused:
            return Outcome.Paused;
        case TestOutcome.InProgress:
            return Outcome.InProgress;
        default:
            return Outcome.None;
    }
}

export function testOutcomeFromString(outcome: string): TestOutcome {
    switch (outcome) {
        case Resources.TestOutcome_Active:
            return TestOutcome.Active;
        case Resources.TestOutcome_Passed:
            return TestOutcome.Passed;
        case Resources.TestOutcome_Failed:
            return TestOutcome.Failed;
        case Resources.TestOutcome_Blocked:
            return TestOutcome.Blocked;
        case Resources.TestOutcome_NotApplicable:
            return TestOutcome.NotApplicable;
        case Resources.TestOutcome_Paused:
            return TestOutcome.Paused;
        case Resources.TestOutcome_InProgress:
            return TestOutcome.InProgress;
        default:
            return TestOutcome.Active;
    }
}

export function testOutcomeToByte(outcome: TestOutcome): number {
    switch (outcome) {
        case TestOutcome.Active:
            return 1;
        case TestOutcome.Passed:
            return Outcome.Passed;
        case TestOutcome.Failed:
            return Outcome.Failed;
        case TestOutcome.Blocked:
            return Outcome.Blocked;
        case TestOutcome.NotApplicable:
            return Outcome.NotApplicable;
        case TestOutcome.Paused:
            return Outcome.Paused;
        case TestOutcome.InProgress:
            return Outcome.InProgress;
        default:
            return 1;
    }
}

export function updateTestCounts(testAnnotation?: ITestAnnotationInfo) {
    if (!testAnnotation) {
        return;
    }

    testAnnotation.passed = 0;
    testAnnotation.failed = 0;
    testAnnotation.notApplicable = 0;
    testAnnotation.other = 0;

    for (const test of testAnnotation.tests.value) {
        if (test.outcome === TestOutcome.Passed) {
            testAnnotation.passed++;
        } else if (test.outcome === TestOutcome.Failed) {
            testAnnotation.failed++;
        } else if (test.outcome === TestOutcome.NotApplicable) {
            testAnnotation.notApplicable++;
        } else {
            testAnnotation.other++;
        }
    }
}

export async function getTestAnnotationData(pageContext: IVssPageContext, teamId: string, workItemIds: number[]): Promise<IWitTestsForKanbanBoard[]> {
    const body = JSON.stringify({ userStoryIds: JSON.stringify(workItemIds) });
    const reponse = await issueTcmRequest<WrappedArray<IWitTestsForKanbanBoard>>(pageContext, teamId, "GetWitTestsForKanbanBoard", body);
    return reponse?.__wrappedArray ?? [];
}

export async function getSuiteForWorkItem(pageContext: IVssPageContext, teamId: string, workItemId: number): Promise<IWitTestsForKanbanBoard> {
    const body = JSON.stringify({ planId: 0, suiteId: 0, requirementId: workItemId });
    return await issueTcmRequest<IWitTestsForKanbanBoard>(pageContext, teamId, "AddWitTestCasesToRequirementSuite", body);
}

export async function issueTcmRequest<T>(pageContext: IVssPageContext, teamId: string, endpointName: string, body: string): Promise<T> {
    const hostPath = pageContext.getService<IVssPageService>("IVssPageService").getData()!.hostPath || "";
    const project = getTFSProject(pageContext)!;
    const requestUrl = combineUrlPaths(encodeURI(hostPath), `${project.id}/_api/_testManagement/${endpointName}?teamId=${teamId}&__v=5`);

    const response = await issueRequest(requestUrl, {
        method: "POST",
        body,
        headers: {
            "Content-Type": "application/json"
        }
    });

    return (await response.json()) as T;
}

type WrappedArray<T> = { __wrappedArray: T[] };

export interface IWitTestsForKanbanBoard {
    /** this is the parent work item */
    requirementId: number;
    testPlanId: number;
    testPoints: {
        outcome: string;
        sequenceNumber: number;
        /** this is the child work item */
        testCaseId: number;
        testCaseTitle: string;
        testPointId: number;
    }[];
    testSuiteId: number;
}
